import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Card, Spinner, Tabs, InfoRow, Alert, Pagination, CheckpointViewer,
} from './shared';

const fmt = (n) => {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;

const fmtDateTime = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};

const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: '2-digit',
    });
};

function extractContentPreview(content) {
    if (!content) return '(empty)';
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return String(content);
    for (const block of content) {
        if (typeof block === 'string') return block;
        if (block.type === 'text' && block.text) return block.text;
        if (block.text) return block.text;
    }
    return `(${content.length} content blocks)`;
}

function extractThinking(content) {
    if (!Array.isArray(content)) return null;
    for (const block of content) {
        if (block.type === 'thinking' && block.thinking) return block.thinking;
    }
    return null;
}


function UserSearchPanel({ onSelectUser, selectedUser }) {
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState({ users: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [offset, setOffset] = useState(0);
    const limit = 20;

    const load = useCallback((searchVal, off) => {
        setLoading(true);
        adminApi.getSessionUsers({ search: searchVal || undefined, limit, offset: off })
            .then(setUsers)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSearch = () => {
        setOffset(0);
        load(search, 0);
        setOpen(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleSelect = (user) => {
        onSelectUser(user);
        setOpen(false);
    };

    const handlePageChange = (newOffset) => {
        setOffset(newOffset);
        load(search, newOffset);
    };

    return (
        <div className="relative">
            {selectedUser ? (
                <div className="flex items-center gap-1.5">
                    <Badge variant="info"><span className="truncate max-w-[140px] inline-block align-bottom">{selectedUser.user_id}</span></Badge>
                    <span className="text-xs text-gray-400">{selectedUser.session_count}</span>
                    <Button variant="ghost" size="xs" onClick={() => onSelectUser(null)} className="ml-auto">x</Button>
                </div>
            ) : (
                <div className="flex items-center gap-1">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search user..."
                            className="h-7 w-full rounded-md border border-gray-200 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                        />
                        <svg className="absolute left-2 top-1.5 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                    </div>
                    <Button size="xs" onClick={handleSearch}>Go</Button>
                </div>
            )}

            {open && !selectedUser && (
                <div className="absolute z-30 top-8 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="p-3"><Spinner text="Loading..." center /></div>
                    ) : users.users.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">No users found</p>
                    ) : (
                        <>
                            {users.users.map((u) => (
                                <div
                                    key={u.user_id}
                                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                                    onClick={() => handleSelect(u)}
                                >
                                    <p className="text-xs font-mono text-gray-900 truncate">{u.user_id}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-[11px] text-gray-400">{u.session_count} sessions</span>
                                        <span className="text-[11px] text-gray-400">{fmtDate(u.last_active)}</span>
                                    </div>
                                </div>
                            ))}
                            <div className="px-2 py-1.5 border-t border-gray-100">
                                <Pagination total={users.total} limit={limit} offset={offset} onChange={handlePageChange} />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SessionCard({ session, isSelected, onClick, showUser }) {
    return (
        <div
            onClick={onClick}
            className={`px-3 py-2.5 cursor-pointer border-b border-gray-50 transition-colors ${
                isSelected ? 'bg-gray-100 border-l-2 border-l-gray-900' : 'hover:bg-gray-50'
            }`}
        >
            <p className="text-sm font-medium text-gray-900 truncate">{session.session_name}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-400">{fmtDate(session.created_at)}</span>
                {showUser && (
                    <Badge variant="outline">{session.user_id}</Badge>
                )}
                {session.total_cost > 0 && (
                    <span className="text-xs font-mono text-gray-500">{fmtCost(session.total_cost)}</span>
                )}
            </div>
        </div>
    );
}

function SessionLeftPanel({ userId, selectedUser, selectedSession, onSelectSession, onSelectUser }) {
    const [sessions, setSessions] = useState({ sessions: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 50;

    const load = useCallback(() => {
        setLoading(true);
        adminApi.getSessionList({
            user_id: userId || undefined,
            search: search || undefined,
            limit,
            offset,
        })
            .then(setSessions)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [userId, search, offset]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setOffset(0); setSearch(''); }, [userId]);

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') { setOffset(0); load(); }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="p-2.5 border-b border-gray-200 space-y-2">
                <UserSearchPanel selectedUser={selectedUser} onSelectUser={onSelectUser} />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Filter sessions..."
                    className="h-7 w-full rounded-md border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="py-8"><Spinner text="Loading..." center /></div>
                ) : sessions.sessions.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No sessions</p>
                ) : (
                    sessions.sessions.map((s) => (
                        <SessionCard
                            key={s.session_id}
                            session={s}
                            isSelected={selectedSession?.session_id === s.session_id}
                            onClick={() => onSelectSession(s)}
                            showUser={!userId}
                        />
                    ))
                )}
            </div>
            <div className="p-2 border-t border-gray-100">
                <Pagination total={sessions.total} limit={limit} offset={offset} onChange={setOffset} />
            </div>
        </div>
    );
}

const ROLE_STYLES = {
    human: { variant: 'info', label: 'User' },
    ai: { variant: 'purple', label: 'AI' },
    tool: { variant: 'default', label: 'Tool' },
    system: { variant: 'default', label: 'System' },
};

const HEADER_KEYS = new Set(['id', 'type', 'content', 'name']);

function UsageInline({ usage }) {
    if (!usage) return null;
    const input = usage.input_tokens || 0;
    const output = usage.output_tokens || 0;
    const total = usage.total_tokens || 0;
    if (!total) return null;
    const cacheRead = usage.input_token_details?.cache_read;
    const cacheCreation = usage.input_token_details?.cache_creation;
    const reasoning = usage.output_token_details?.reasoning;
    return (
        <>
            <span className="text-xs font-mono text-gray-500">In:{fmt(input)}</span>
            <span className="text-xs font-mono text-gray-500">Out:{fmt(output)}</span>
            <span className="text-xs font-mono font-semibold text-gray-700">{fmt(total)}</span>
            {cacheRead > 0 && <span className="text-xs font-mono text-blue-500">cache:{fmt(cacheRead)}</span>}
            {cacheCreation > 0 && <span className="text-xs font-mono text-blue-500">cache_w:{fmt(cacheCreation)}</span>}
            {reasoning > 0 && <span className="text-xs font-mono text-amber-500">reason:{fmt(reasoning)}</span>}
        </>
    );
}

function FieldBlock({ label, value }) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
        return (
            <details className="mt-2">
                <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">{label}</summary>
                <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[400px] overflow-y-auto bg-gray-50 rounded-md p-2 mt-1">{JSON.stringify(value, null, 2)}</pre>
            </details>
        );
    }
    return <InfoRow label={label} value={String(value)} mono />;
}

function ContentView({ content, thinking, expanded }) {
    const [viewMode, setViewMode] = useState('markdown');
    const preview = extractContentPreview(content);

    if (!expanded) {
        return (
            <>
                <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-2">{preview}</p>
                {thinking && (
                    <p className="text-xs text-purple-600 italic mt-1 line-clamp-2 whitespace-pre-wrap">{thinking}</p>
                )}
            </>
        );
    }

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-1 mb-2">
                <button
                    type="button"
                    onClick={() => setViewMode('markdown')}
                    className={`h-6 px-2 text-[11px] rounded border transition-colors ${
                        viewMode === 'markdown'
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                    }`}
                >
                    Markdown
                </button>
                <button
                    type="button"
                    onClick={() => setViewMode('raw')}
                    className={`h-6 px-2 text-[11px] rounded border transition-colors ${
                        viewMode === 'raw'
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                    }`}
                >
                    Raw
                </button>
            </div>
            {viewMode === 'markdown' ? (
                <div className="prose prose-sm max-w-none text-gray-700">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {preview}
                    </ReactMarkdown>
                    {thinking && (
                        <details className="mt-2">
                            <summary className="text-xs font-medium text-purple-500 cursor-pointer hover:text-purple-700">Thinking</summary>
                            <div className="prose prose-sm max-w-none text-purple-700 mt-1 pl-3 border-l-2 border-purple-200">
                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                    {thinking}
                                </ReactMarkdown>
                            </div>
                        </details>
                    )}
                </div>
            ) : (
                <div>
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[600px] overflow-y-auto bg-gray-50 rounded-md p-2">
                        {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                    </pre>
                    {thinking && (
                        <details className="mt-2">
                            <summary className="text-xs font-medium text-purple-500 cursor-pointer hover:text-purple-700">Thinking (raw)</summary>
                            <pre className="text-xs font-mono text-purple-600 whitespace-pre-wrap max-h-[400px] overflow-y-auto bg-purple-50 rounded-md p-2 mt-1">{thinking}</pre>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}

function MessageCard({ message, expanded, onToggle, scrollRef }) {
    const role = ROLE_STYLES[message.type] || ROLE_STYLES.system;
    const thinking = extractThinking(message.content);
    const usage = message.usage_metadata;
    const modelName = message.response_metadata?.model_name || message.response_metadata?.model;
    const toolCalls = message.tool_calls || [];
    const isStart = message.type === 'human';
    const isFinal = message.type === 'ai' && !!(message.additional_kwargs?.is_final);
    const extraKeys = Object.keys(message).filter((k) => !HEADER_KEYS.has(k));

    const borderClass = isFinal
        ? (expanded ? 'border-emerald-400 bg-emerald-50/30' : 'border-emerald-300 bg-emerald-50/20 hover:border-emerald-400')
        : isStart
            ? (expanded ? 'border-blue-400 bg-blue-50/30' : 'border-blue-300 bg-blue-50/20 hover:border-blue-400')
            : (expanded ? 'border-gray-300 bg-white' : 'border-gray-200 hover:border-gray-300');

    return (
        <div
            ref={scrollRef}
            className={`rounded-lg border-2 transition-colors cursor-pointer ${borderClass}`}
            onClick={onToggle}
        >
            <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant={role.variant}>{role.label}</Badge>
                    {isStart && <Badge variant="info">Start</Badge>}
                    {isFinal && <Badge variant="success">Final</Badge>}
                    {message.name && <Badge variant="outline">{message.name}</Badge>}
                    {toolCalls.length > 0 && (
                        <Badge variant="warning">{toolCalls.length} tool calls</Badge>
                    )}
                    {modelName && <span className="text-xs text-gray-400 font-mono">{modelName}</span>}
                    <UsageInline usage={usage} />
                    <span className="text-xs text-gray-400 font-mono ml-auto truncate max-w-[200px]">{message.id}</span>
                </div>
                {toolCalls.length > 0 && (
                    <div className="space-y-1.5 mb-1.5">
                        {toolCalls.map((tc, i) => (
                            <div key={tc.id || i}>
                                <span className="text-xs font-mono text-orange-600 bg-orange-50 rounded px-1.5 py-0.5">{tc.name}</span>
                                {expanded && tc.args && Object.keys(tc.args).length > 0 && (
                                    <pre className="mt-1 text-xs font-mono text-gray-600 bg-gray-50 rounded p-2 overflow-x-auto max-h-[200px] overflow-y-auto">{JSON.stringify(tc.args, null, 2)}</pre>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <ContentView content={message.content} thinking={thinking} expanded={expanded} />
            </div>
            {expanded && (
                <div className="px-4 pb-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                    {extraKeys.map((key) => (
                        <FieldBlock key={key} label={key} value={message[key]} />
                    ))}
                </div>
            )}
        </div>
    );
}

const TYPE_FILTERS = [
    { id: '', label: 'All' },
    { id: 'human', label: 'User' },
    { id: 'ai', label: 'AI' },
    { id: 'tool', label: 'Tool' },
];

function MessagesTab({ sessionId, expandedMsgId, onMsgToggle }) {
    const [data, setData] = useState({ messages: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const scrolledRef = useRef(false);
    const limit = 50;

    useEffect(() => {
        setOffset(0);
        setTypeFilter('');
        scrolledRef.current = false;
    }, [sessionId]);

    useEffect(() => {
        setLoading(true);
        adminApi.getSessionMessages(sessionId, {
            type: typeFilter || undefined,
            limit,
            offset,
        })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [sessionId, typeFilter, offset]);

    const scrollToMsg = useCallback((el) => {
        if (el && !scrolledRef.current && expandedMsgId) {
            scrolledRef.current = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [expandedMsgId]);

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-1.5">
                {TYPE_FILTERS.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => { setTypeFilter(f.id); setOffset(0); }}
                        className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                            typeFilter === f.id
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="text-xs text-gray-400 ml-auto">{data.total} messages</span>
            </div>

            {loading ? (
                <Spinner text="Loading messages..." center />
            ) : data.messages.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No messages</p>
            ) : (
                <div className="space-y-2">
                    {data.messages.map((msg, idx) => {
                        const msgId = msg.id || String(idx);
                        const isExpanded = expandedMsgId === msgId;
                        return (
                            <MessageCard
                                key={msgId}
                                message={msg}
                                expanded={isExpanded}
                                onToggle={() => onMsgToggle(isExpanded ? null : msgId)}
                                scrollRef={isExpanded ? scrollToMsg : undefined}
                            />
                        );
                    })}
                </div>
            )}

            <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
        </div>
    );
}

function TokenUsageTab({ sessionId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getSessionTokenUsage(sessionId)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [sessionId]);

    if (loading) return <Spinner text="Loading token usage..." center />;
    if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

    const { summary, logs, by_agent = [] } = data;
    const maxCost = Math.max(...logs.map((l) => l.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Requests</p>
                    <p className="text-lg font-semibold text-gray-900">{fmt(summary.total_requests)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Tokens</p>
                    <p className="text-lg font-semibold text-gray-900">{fmt(summary.total_tokens)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-lg font-semibold text-gray-900">{fmtCost(summary.total_cost)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Models Used</p>
                    <p className="text-lg font-semibold text-gray-900">{summary.models_used.length}</p>
                    <p className="text-xs text-gray-400 truncate">{summary.models_used.join(', ')}</p>
                </div>
            </div>

            {by_agent.length > 1 && (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-200">
                        By Agent (includes sub-agent usage)
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Agent</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Calls</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">In</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Out</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cache</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Total</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cost</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">%</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {by_agent.map((a) => (
                                <tr key={a.agent_name} className="hover:bg-gray-50">
                                    <td className="px-3 py-1.5 text-xs text-gray-700 font-medium">{a.agent_name}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(a.requests)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(a.input_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(a.output_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs text-gray-400">{fmt(a.cache_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs font-semibold">{fmt(a.total_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs font-mono">{fmtCost(a.total_cost)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs text-gray-500">
                                        {summary.total_cost > 0 ? `${Math.round((a.total_cost / summary.total_cost) * 100)}%` : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {logs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No token usage logs</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Agent</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">In</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Out</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cost</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Time</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {logs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50">
                                    <td className="px-3 py-1.5 font-mono text-xs">{log.model_name}</td>
                                    <td className="px-3 py-1.5 text-xs text-gray-600">{log.agent_name}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(log.input_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(log.output_tokens)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs font-mono">{fmtCost(log.total_cost)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs text-gray-500">
                                        {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function SessionRightPanel({ session, expandedMsgId, onMsgToggle }) {
    const [activeTab, setActiveTab] = useState('messages');

    const tabs = [
        { id: 'messages', label: 'Messages' },
        { id: 'token-usage', label: 'Token Usage' },
        { id: 'checkpoints', label: 'Checkpoints' },
    ];

    if (!session) {
        return (
            <div className="flex items-center justify-center py-32 text-sm text-gray-400">
                Select a session to view messages
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="shrink-0 bg-white border-b border-gray-200">
                <div className="px-5 py-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-semibold text-gray-900 truncate">{session.session_name}</h3>
                                <a
                                    href={`/admin-view/${session.session_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors shrink-0"
                                >
                                    View Chat
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                </a>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-xs text-gray-400 font-mono truncate">{session.user_id}</span>
                                <span className="text-xs text-gray-400 font-mono truncate">{session.session_id}</span>
                                <span className="text-xs text-gray-400">{fmtDateTime(session.created_at)}</span>
                            </div>
                        </div>
                        <div className="text-right shrink-0 ml-4">
                            <p className="text-xs text-gray-400">Tokens</p>
                            <p className="text-sm font-semibold">{fmt(session.total_tokens)}</p>
                        </div>
                    </div>
                    <div className="px-5 py-2 bg-gray-50 border-b text-xs font-mono space-y-0.5 overflow-x-auto">
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-gray-400 w-12 text-right shrink-0">Tokens</span>
                            <span className="w-20 text-right">{fmt(session.input_tokens)}</span>
                            <span className="text-gray-300 shrink-0">+</span>
                            <span className="w-20 text-right">{fmt(session.output_tokens)}</span>
                            <span className="text-gray-300 shrink-0">+</span>
                            <span className="w-20 text-right">{fmt(session.cache_tokens)}</span>
                            <span className="text-gray-300 shrink-0">=</span>
                            <span className="w-20 text-right font-semibold">{fmt(session.total_tokens)}</span>
                            {session.reasoning_tokens > 0 && (
                                <span className="text-gray-400 ml-2">(reasoning: {fmt(session.reasoning_tokens)})</span>
                            )}
                        </div>
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-gray-400 w-12 text-right shrink-0">Cost</span>
                            <span className="w-20 text-right">{fmtCost(session.input_cost)}</span>
                            <span className="text-gray-300 shrink-0">+</span>
                            <span className="w-20 text-right">{fmtCost(session.output_cost)}</span>
                            <span className="text-gray-300 shrink-0">+</span>
                            <span className="w-20 text-right">{fmtCost(session.cache_cost)}</span>
                            <span className="text-gray-300 shrink-0">=</span>
                            <span className="w-20 text-right font-semibold">{fmtCost(session.total_cost)}</span>
                        </div>
                        <div className="flex items-center gap-1 whitespace-nowrap text-gray-400">
                            <span className="w-12 shrink-0"></span>
                            <span className="w-20 text-center">input</span>
                            <span className="invisible">+</span>
                            <span className="w-20 text-center">output</span>
                            <span className="invisible">+</span>
                            <span className="w-20 text-center">cache</span>
                            <span className="invisible">=</span>
                            <span className="w-20 text-center">total</span>
                        </div>
                    </div>
                </div>
                <div className="px-5 pt-2 pb-0">
                    <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
                </div>
            </div>
            <div className="flex-1 overflow-hidden">
                {activeTab === 'messages' && (
                    <div className="h-full overflow-y-auto px-5 py-4">
                        <MessagesTab
                            sessionId={session.session_id}
                            expandedMsgId={expandedMsgId}
                            onMsgToggle={onMsgToggle}
                        />
                    </div>
                )}
                {activeTab === 'token-usage' && (
                    <div className="h-full overflow-y-auto px-5 py-4">
                        <TokenUsageTab sessionId={session.session_id} />
                    </div>
                )}
                {activeTab === 'checkpoints' && (
                    <div className="h-full px-5 py-4">
                        <CheckpointViewer threadId={session.session_id} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminSessions() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [initialLoading, setInitialLoading] = useState(false);
    const initializedRef = useRef(false);

    const urlSessionId = searchParams.get('session_id');
    const urlMsgId = searchParams.get('msg');

    useEffect(() => {
        if (initializedRef.current || !urlSessionId) return;
        initializedRef.current = true;
        setInitialLoading(true);
        adminApi.getSessionList({ session_id: urlSessionId, limit: 1 })
            .then((res) => {
                if (res.sessions?.length > 0) {
                    setSelectedSession(res.sessions[0]);
                }
            })
            .catch(() => {})
            .finally(() => setInitialLoading(false));
    }, [urlSessionId]);

    const updateParams = useCallback((sessionId, msgId) => {
        setSearchParams((prev) => {
            if (sessionId) prev.set('session_id', sessionId);
            else prev.delete('session_id');
            if (msgId) prev.set('msg', msgId);
            else prev.delete('msg');
            return prev;
        }, { replace: true });
    }, [setSearchParams]);

    const handleSelectSession = useCallback((session) => {
        setSelectedSession(session);
        updateParams(session?.session_id, null);
    }, [updateParams]);

    const handleSelectUser = useCallback((user) => {
        setSelectedUser(user);
        setSelectedSession(null);
        updateParams(null, null);
    }, [updateParams]);

    const handleMsgToggle = useCallback((msgId) => {
        updateParams(selectedSession?.session_id, msgId);
    }, [updateParams, selectedSession]);

    if (initialLoading) {
        return (
            <div className="flex items-center justify-center h-screen -mx-6 -my-8">
                <Spinner size="lg" text="Loading session..." center />
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-4rem)] border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="w-72 shrink-0 border-r border-gray-200 bg-white">
                <SessionLeftPanel
                    userId={selectedUser?.user_id}
                    selectedUser={selectedUser}
                    selectedSession={selectedSession}
                    onSelectSession={handleSelectSession}
                    onSelectUser={handleSelectUser}
                />
            </div>
            <div className="flex-1 min-w-0 bg-white overflow-hidden">
                <SessionRightPanel
                    session={selectedSession}
                    expandedMsgId={urlMsgId}
                    onMsgToggle={handleMsgToggle}
                />
            </div>
        </div>
    );
}
